/* Orya — product media carousel, Portuguese only.
 * Product media are tagged through the media alt text: "[pt]" or "#pt" for
 * Portuguese, "[en]" or "#en" for English, nothing for images that serve both.
 * (The store uses the square-bracket form; the hash form is kept because the
 * earlier version of this file only looked for that one.)
 * Since September 2026 the storefront shows only the Portuguese and untagged
 * slides, whichever language the visitor picks in the header: English images
 * stay in the product but are never displayed. Products with no Portuguese
 * image fall back to showing all of their media, so a product is never left
 * without pictures.
 */
(() => {
  const SUPPORTED = ['en', 'pt'];
  const MEDIA_LANG = 'pt';

  const langOf = (alt) => {
    const a = (alt || '').toLowerCase();
    if (a.indexOf('[pt]') !== -1 || a.indexOf('#pt') !== -1) return 'pt';
    if (a.indexOf('[en]') !== -1 || a.indexOf('#en') !== -1) return 'en';
    return 'both';
  };

  const init = async () => {
    const match = window.location.pathname.match(/\/products\/([^/?#]+)/);
    if (!match) return;
    const oldCarousel = document.querySelector('.orya-product-media [data-orya-carousel]');
    if (!oldCarousel) return;

    let media;
    try {
      const res = await fetch('/products/' + match[1] + '.js');
      if (!res.ok) return;
      media = (await res.json()).media || [];
    } catch (_) { return; }
    if (!media.length) return;

    /* Take over: clone strips the old script's listeners; its timer keeps
     * running on detached nodes, which has no visible effect. */
    const carousel = oldCarousel.cloneNode(true);
    oldCarousel.replaceWith(carousel);

    const track = carousel.querySelector('[data-orya-carousel-track]');
    if (!track) return;
    let slides = Array.from(track.querySelectorAll('[data-orya-carousel-slide]'));

    /* The section renders at most 8 media; append the rest as image slides. */
    for (let i = slides.length; i < media.length; i++) {
      const m = media[i];
      const src = m.src || (m.preview_image && m.preview_image.src);
      if (!src || (m.media_type && m.media_type !== 'image')) continue;
      const sep = src.indexOf('?') === -1 ? '?' : '&';
      const fig = document.createElement('figure');
      fig.className = 'orya-product-media-card';
      fig.setAttribute('data-orya-carousel-slide', '');
      const a = document.createElement('a');
      a.className = 'orya-product-media-link';
      a.href = src + sep + 'width=2400';
      a.target = '_blank';
      a.rel = 'noopener';
      a.setAttribute('aria-label', 'Abrir pré-visualização ' + (i + 1));
      const img = document.createElement('img');
      img.loading = 'lazy';
      img.src = src + sep + 'width=1600';
      img.alt = '';
      a.appendChild(img);
      fig.appendChild(a);
      track.appendChild(fig);
    }
    slides = Array.from(track.querySelectorAll('[data-orya-carousel-slide]'));

    /* Tag every slide with its language, taken from the media alt text. */
    slides.forEach((slide, i) => {
      slide.dataset.oryaMediaLang = media[i] ? langOf(media[i].alt) : 'both';
    });

    const prev = carousel.querySelector('[data-orya-carousel-prev]');
    const next = carousel.querySelector('[data-orya-carousel-next]');
    const dotsWrap = carousel.querySelector('[data-orya-carousel-dots]');
    const count = carousel.querySelector('[data-orya-carousel-count]');
    if (count) count.setAttribute('data-no-localize', '');

    let visible = slides;
    let index = 0;
    let paused = false;

    const render = () => {
      track.style.transform = 'translateX(' + (-index * 100) + '%)';
      if (dotsWrap) {
        Array.from(dotsWrap.querySelectorAll('button')).forEach((dot, dotIndex) => {
          dot.classList.toggle('is-active', dotIndex === index);
          dot.setAttribute('aria-current', dotIndex === index ? 'true' : 'false');
        });
      }
      if (count) count.textContent = (index + 1) + ' / ' + visible.length;
    };

    const go = (nextIndex, userAction) => {
      if (!visible.length) return;
      index = (nextIndex + visible.length) % visible.length;
      if (userAction) paused = true;
      render();
    };

    const buildDots = () => {
      if (!dotsWrap) return;
      dotsWrap.innerHTML = '';
      if (visible.length < 2) return;
      visible.forEach((_, dotIndex) => {
        const dot = document.createElement('button');
        dot.className = 'orya-carousel-dot';
        dot.type = 'button';
        dot.setAttribute('aria-label', 'Mostrar pré-visualização ' + (dotIndex + 1));
        dot.addEventListener('click', () => go(dotIndex, true));
        dotsWrap.appendChild(dot);
      });
    };

    const applyMedia = () => {
      let matching = slides.filter((slide) => {
        const slideLang = slide.dataset.oryaMediaLang || 'both';
        return slideLang === 'both' || slideLang === MEDIA_LANG;
      });
      if (!matching.length) matching = slides;
      slides.forEach((slide) => { slide.style.display = matching.indexOf(slide) !== -1 ? '' : 'none'; });
      visible = matching;
      index = 0;
      buildDots();
      render();
      const many = visible.length > 1;
      if (prev) prev.style.display = many ? '' : 'none';
      if (next) next.style.display = many ? '' : 'none';
      if (count) count.style.display = many ? '' : 'none';
      if (dotsWrap) dotsWrap.style.display = many ? '' : 'none';
    };

    if (prev) prev.addEventListener('click', () => go(index - 1, true));
    if (next) next.addEventListener('click', () => go(index + 1, true));
    carousel.addEventListener('mouseenter', () => { paused = true; });
    carousel.addEventListener('focusin', () => { paused = true; });

    applyMedia();

    /* The header EN/PT switch no longer changes the images; it only re-runs the
     * layout in case the surrounding text changed the slide sizes. */
    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-orya-lang-button]');
      if (button && SUPPORTED.indexOf(button.dataset.oryaLangButton) !== -1) window.setTimeout(render, 0);
    });

    if (carousel.dataset.autoplay === 'true') {
      window.setInterval(() => {
        if (!paused && visible.length > 1) go(index + 1, false);
      }, 4500);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
